import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, Alert, Platform } from 'react-native';
import { getFirestore, collection, addDoc, getDocs, updateDoc, doc, query, orderBy, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useAdminThemeContext } from '../contexts/AdminThemeContext';
import { getAdminSearchPlaceholderColor, setWebPlaceholderColor } from '../utils/setWebPlaceholderColor';

const MAX_ACTIVE_URLS = 5;
const COMMUNITY_DIR_SEARCH_IDS = ['community-dir-name', 'community-dir-date', 'community-dir-users'];

export const CommunityDirectoryManagement = () => {
  const { styles: adminStyles, colors, isDarkMode } = useAdminThemeContext();
  const [inviteUrls, setInviteUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newUrlData, setNewUrlData] = useState({
    name: '',
    expirationDate: '',
    maxUsers: '',
    expirationType: 'date',
  });

  const searchPlaceholderColor = getAdminSearchPlaceholderColor(isDarkMode);

  useEffect(() => {
    COMMUNITY_DIR_SEARCH_IDS.forEach((id) => {
      setWebPlaceholderColor(id, searchPlaceholderColor);
    });
  }, [searchPlaceholderColor]);

  const pillStyle = (active) => ({
    backgroundColor: active ? '#2ecc71' : colors.cardBgSubtle,
    borderWidth: active ? 0 : 1,
    borderColor: colors.cardBorder,
    paddingHorizontal: 15,
    paddingVertical: 8,
    marginRight: 10,
    marginBottom: 10,
    borderRadius: 8,
  });

  const pillTextStyle = (active) => ({
    color: active ? '#ffffff' : colors.textPrimary,
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: active ? '600' : '500',
  });

  useEffect(() => {
    loadInviteUrls();
  }, []);

  const loadInviteUrls = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const urlsSnapshot = await getDocs(
        query(collection(db, 'inviteUrls'), orderBy('createdAt', 'desc'))
      );
      const urlsData = urlsSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setInviteUrls(urlsData);
    } catch (error) {
      console.error('Error loading invite URLs:', error);
      Alert.alert('Error', 'Failed to load invite URLs');
    } finally {
      setLoading(false);
    }
  };

  const generateInviteCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const getBaseUrl = () => {
    if (Platform.OS === 'web') {
      return window.location.origin;
    }
    return 'https://venue-report-card.web.app';
  };

  const createInviteUrl = async () => {
    try {
      const activeUrls = inviteUrls.filter(url => url.isActive && !isUrlExpired(url));
      if (activeUrls.length >= MAX_ACTIVE_URLS) {
        Alert.alert('Limit Reached', `You can only have ${MAX_ACTIVE_URLS} active invite URLs at a time. Please deactivate one first.`);
        return;
      }

      if (!newUrlData.name.trim()) {
        Alert.alert('Error', 'Please enter a name for this invite URL');
        return;
      }

      if (newUrlData.expirationType === 'date' && !newUrlData.expirationDate) {
        Alert.alert('Error', 'Please select an expiration date');
        return;
      }

      if (newUrlData.expirationType === 'users' && (!newUrlData.maxUsers || parseInt(newUrlData.maxUsers, 10) <= 0)) {
        Alert.alert('Error', 'Please enter a valid number of users');
        return;
      }

      setLoading(true);
      const db = getFirestore();
      const auth = getAuth();
      const inviteCode = generateInviteCode();
      const baseUrl = getBaseUrl();
      const fullUrl = `${baseUrl}/community?inviteCode=${inviteCode}`;

      const urlData = {
        name: newUrlData.name.trim(),
        inviteCode,
        url: fullUrl,
        isActive: true,
        expirationType: newUrlData.expirationType,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid,
        usedCount: 0,
        usedBy: [],
      };

      if (newUrlData.expirationType === 'date') {
        const dateParts = newUrlData.expirationDate.split('/');
        if (dateParts.length === 3) {
          const month = parseInt(dateParts[0], 10) - 1;
          const day = parseInt(dateParts[1], 10);
          const year = parseInt(dateParts[2], 10);
          const expirationDate = new Date(year, month, day);
          expirationDate.setHours(23, 59, 59, 999);
          urlData.expirationDate = expirationDate;
        } else {
          Alert.alert('Error', 'Please enter date in MM/DD/YYYY format');
          setLoading(false);
          return;
        }
      }

      if (newUrlData.expirationType === 'users') {
        urlData.maxUsers = parseInt(newUrlData.maxUsers, 10);
      }

      await addDoc(collection(db, 'inviteUrls'), urlData);

      setNewUrlData({
        name: '',
        expirationDate: '',
        maxUsers: '',
        expirationType: 'date',
      });

      Alert.alert('Success', 'Invite URL created successfully!');
      loadInviteUrls();
    } catch (error) {
      console.error('Error creating invite URL:', error);
      Alert.alert('Error', 'Failed to create invite URL');
    } finally {
      setLoading(false);
    }
  };

  const deactivateUrl = async (urlId) => {
    const confirmMessage = 'Are you sure you want to deactivate this invite URL? It will stop working immediately.';

    const runDeactivate = async () => {
      try {
        const db = getFirestore();
        await updateDoc(doc(db, 'inviteUrls', urlId), {
          isActive: false,
          deactivatedAt: serverTimestamp(),
        });
        Alert.alert('Success', 'Invite URL deactivated successfully');
        loadInviteUrls();
      } catch (error) {
        console.error('Error deactivating URL:', error);
        Alert.alert('Error', 'Failed to deactivate invite URL');
      }
    };

    if (Platform.OS === 'web') {
      if (window.confirm(confirmMessage)) {
        await runDeactivate();
      }
    } else {
      Alert.alert(
        'Confirm Deactivation',
        confirmMessage,
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Deactivate', style: 'destructive', onPress: runDeactivate },
        ]
      );
    }
  };

  const deleteUrl = async (urlId) => {
    const confirmMessage = 'Are you sure you want to delete this invite URL? This action cannot be undone.';

    const runDelete = async () => {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'inviteUrls', urlId));
        Alert.alert('Success', 'Invite URL deleted successfully');
        loadInviteUrls();
      } catch (error) {
        console.error('Error deleting URL:', error);
        Alert.alert('Error', 'Failed to delete invite URL');
      }
    };

    if (Platform.OS === 'web') {
      if (window.confirm(confirmMessage)) {
        await runDelete();
      }
    } else {
      Alert.alert(
        'Confirm Deletion',
        confirmMessage,
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Delete', style: 'destructive', onPress: runDelete },
        ]
      );
    }
  };

  const copyToClipboard = (text) => {
    if (Platform.OS === 'web') {
      navigator.clipboard.writeText(text).then(() => {
        Alert.alert('Copied', 'URL copied to clipboard');
      });
    } else {
      Alert.alert('Copy', `URL: ${text}`);
    }
  };

  const isUrlExpired = (url) => {
    if (!url.isActive) return true;

    if (url.expirationType === 'date' && url.expirationDate) {
      const expirationDate = url.expirationDate.toDate ? url.expirationDate.toDate() : new Date(url.expirationDate);
      return new Date() > expirationDate;
    }

    if (url.expirationType === 'users' && url.maxUsers) {
      return (url.usedCount || 0) >= url.maxUsers;
    }

    return false;
  };

  const getExpirationStatus = (url) => {
    if (!url.isActive) return { text: 'Deactivated', color: '#e74c3c' };
    if (isUrlExpired(url)) return { text: 'Expired', color: '#e74c3c' };

    if (url.expirationType === 'date' && url.expirationDate) {
      const expirationDate = url.expirationDate.toDate ? url.expirationDate.toDate() : new Date(url.expirationDate);
      const formattedDate = expirationDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      return { text: `Expires ${formattedDate}`, color: '#2ecc71' };
    }

    if (url.expirationType === 'users' && url.maxUsers) {
      const remaining = url.maxUsers - (url.usedCount || 0);
      return { text: `${remaining} uses remaining`, color: '#2ecc71' };
    }

    return { text: 'Active', color: '#2ecc71' };
  };

  const activeUrls = inviteUrls.filter(url => url.isActive && !isUrlExpired(url));
  const inactiveUrls = inviteUrls.filter(url => !url.isActive || isUrlExpired(url));

  return (
    <ScrollView style={{ width: '100%' }}>
      <View style={{ padding: 20 }}>
        <Text style={{
          fontSize: 28,
          color: colors.textPrimary,
          fontFamily: 'VastagoGrotesk',
          marginBottom: 20,
          textAlign: 'center',
          fontWeight: '700',
        }}>
          Community Directory Management
        </Text>

        <View style={[adminStyles.reportCard, { marginBottom: 30 }]}>
          <Text style={[adminStyles.cardTitle, { marginBottom: 15 }]}>Create New Invite URL</Text>

          <View style={{ marginBottom: 15 }}>
            <Text style={[adminStyles.detailLabel, { marginBottom: 5 }]}>Name/Label:</Text>
            <TextInput
              nativeID="community-dir-name"
              style={[adminStyles.userSearchInput, { marginBottom: 10 }]}
              value={newUrlData.name}
              onChangeText={(text) => setNewUrlData({ ...newUrlData, name: text })}
              placeholder="e.g., November 2024 Campaign"
              placeholderTextColor={searchPlaceholderColor}
            />
          </View>

          <View style={{ marginBottom: 15 }}>
            <Text style={[adminStyles.detailLabel, { marginBottom: 5 }]}>Expiration Type:</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
              {['date', 'users', 'none'].map((type) => (
                <TouchableOpacity
                  key={type}
                  style={pillStyle(newUrlData.expirationType === type)}
                  onPress={() => setNewUrlData({ ...newUrlData, expirationType: type })}
                >
                  <Text style={pillTextStyle(newUrlData.expirationType === type)}>
                    {type === 'date' ? 'Date' : type === 'users' ? 'Limited Users' : 'No Expiration'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {newUrlData.expirationType === 'date' && (
            <View style={{ marginBottom: 15 }}>
              <Text style={[adminStyles.detailLabel, { marginBottom: 5 }]}>Expiration Date:</Text>
              <TextInput
                nativeID="community-dir-date"
                style={[adminStyles.userSearchInput, { marginBottom: 10 }]}
                value={newUrlData.expirationDate}
                onChangeText={(text) => setNewUrlData({ ...newUrlData, expirationDate: text })}
                placeholder="MM/DD/YYYY (e.g., 11/29/2025)"
                placeholderTextColor={searchPlaceholderColor}
              />
            </View>
          )}

          {newUrlData.expirationType === 'users' && (
            <View style={{ marginBottom: 15 }}>
              <Text style={[adminStyles.detailLabel, { marginBottom: 5 }]}>Max Users:</Text>
              <TextInput
                nativeID="community-dir-users"
                style={[adminStyles.userSearchInput, { marginBottom: 10 }]}
                value={newUrlData.maxUsers}
                onChangeText={(text) => setNewUrlData({ ...newUrlData, maxUsers: text.replace(/[^0-9]/g, '') })}
                placeholder="e.g., 10"
                keyboardType="numeric"
                placeholderTextColor={searchPlaceholderColor}
              />
            </View>
          )}

          <TouchableOpacity
            style={[adminStyles.actionButton, {
              backgroundColor: activeUrls.length >= MAX_ACTIVE_URLS ? '#95a5a6' : '#2ecc71',
              marginTop: 10,
            }]}
            onPress={createInviteUrl}
            disabled={loading || activeUrls.length >= MAX_ACTIVE_URLS}
          >
            <Text style={adminStyles.actionButtonText}>
              {loading ? 'Creating...' : `Create Invite URL (${activeUrls.length}/${MAX_ACTIVE_URLS} active)`}
            </Text>
          </TouchableOpacity>
        </View>

        <View style={{ marginBottom: 30 }}>
          <Text style={[adminStyles.cardTitle, { marginBottom: 15 }]}>
            Active Invite URLs ({activeUrls.length})
          </Text>
          {activeUrls.length === 0 ? (
            <Text style={[adminStyles.emptyStateText, { textAlign: 'center' }]}>
              No active invite URLs
            </Text>
          ) : (
            activeUrls.map((url) => {
              const status = getExpirationStatus(url);
              return (
                <View key={url.id} style={[adminStyles.reportCard, { marginBottom: 15 }]}>
                  <View style={{ marginBottom: 10 }}>
                    <Text style={[adminStyles.userName, { marginBottom: 5 }]}>{url.name}</Text>
                    <Text style={[adminStyles.detailText, { marginBottom: 5 }]}>{url.url}</Text>
                    <Text style={[adminStyles.detailText, { color: status.color, marginBottom: 5 }]}>
                      Status: {status.text}
                    </Text>
                    <Text style={adminStyles.detailText}>
                      Used: {url.usedCount || 0} {url.maxUsers ? `of ${url.maxUsers}` : ''}
                    </Text>
                    <Text style={adminStyles.detailText}>
                      Created: {url.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                    <TouchableOpacity
                      style={[adminStyles.actionButton, { backgroundColor: '#3498db', paddingHorizontal: 15, paddingVertical: 8 }]}
                      onPress={() => copyToClipboard(url.url)}
                    >
                      <Text style={adminStyles.actionButtonText}>Copy URL</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[adminStyles.actionButton, { backgroundColor: '#e74c3c', paddingHorizontal: 15, paddingVertical: 8 }]}
                      onPress={() => deactivateUrl(url.id)}
                    >
                      <Text style={adminStyles.actionButtonText}>Deactivate</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[adminStyles.actionButton, { backgroundColor: '#95a5a6', paddingHorizontal: 15, paddingVertical: 8 }]}
                      onPress={() => deleteUrl(url.id)}
                    >
                      <Text style={adminStyles.actionButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })
          )}
        </View>

        {inactiveUrls.length > 0 && (
          <View>
            <Text style={[adminStyles.cardTitle, { marginBottom: 15 }]}>
              Inactive/Expired Invite URLs ({inactiveUrls.length})
            </Text>
            {inactiveUrls.map((url) => {
              const status = getExpirationStatus(url);
              return (
                <View key={url.id} style={[adminStyles.reportCard, { marginBottom: 15, opacity: 0.7 }]}>
                  <View style={{ marginBottom: 10 }}>
                    <Text style={[adminStyles.userName, { marginBottom: 5 }]}>{url.name}</Text>
                    <Text style={[adminStyles.detailText, { marginBottom: 5 }]}>{url.url}</Text>
                    <Text style={[adminStyles.detailText, { color: status.color, marginBottom: 5 }]}>
                      Status: {status.text}
                    </Text>
                    <Text style={adminStyles.detailText}>
                      Used: {url.usedCount || 0} {url.maxUsers ? `of ${url.maxUsers}` : ''}
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                    <TouchableOpacity
                      style={[adminStyles.actionButton, { backgroundColor: '#95a5a6', paddingHorizontal: 15, paddingVertical: 8 }]}
                      onPress={() => deleteUrl(url.id)}
                    >
                      <Text style={adminStyles.actionButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </View>
    </ScrollView>
  );
};
